import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Download, 
  Send, 
  CheckCircle, 
  AlertCircle,
  User,
  MessageSquare
} from 'lucide-react';
import { AnimatedSection } from '../ui/AnimatedSection';
import { ContactFormData, FormErrors } from '../../types';

export const Contact: React.FC = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case 'name':
        return value.length < 2 ? 'Le nom doit contenir au moins 2 caractères' : '';
      case 'email':
        return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? 'Email invalide' : '';
      case 'subject':
        return value.length < 3 ? 'Le sujet doit contenir au moins 3 caractères' : '';
      case 'message':
        return '';
      default:
        return '';
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Validation complète
    const newErrors: FormErrors = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key as keyof ContactFormData]);
      if (error) newErrors[key] = error;
    });
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsSubmitting(false);
      return;
    }

    // Envoi via Formspree
    try {
      const response = await fetch('https://formspree.io/f/xrbkoaog', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
        }),
      });
      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
        setErrors({});
        setTimeout(() => setIsSubmitted(false), 5000);
      } else {
        setErrors({ submit: "Erreur lors de l'envoi. Veuillez réessayer." });
      }
    } catch (error) {
      setErrors({ submit: "Erreur lors de l'envoi. Veuillez réessayer." });
    }
    setIsSubmitting(false);
  };

  const handleDownloadCV = () => {
    // Simulation du téléchargement
    const link = document.createElement('a');
    link.href = '/CV Enzo.pdf'; // Vous devrez ajouter ce fichier dans public/
    link.download = 'CV_Enzo_Marques.pdf';
    link.click();
  };

  return (
    <section id="contact" className="py-20 bg-slate-50 dark:bg-slate-900 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0">
        <div className="absolute top-20 right-1/4 w-1 h-1 bg-indigo-400 rounded-full opacity-20 animate-float-dots" />
        <div className="absolute top-1/2 left-10 w-1.5 h-1.5 bg-primary-400 rounded-full opacity-15 animate-float-dots" 
             style={{ animationDelay: '-2s' }} />
        <div className="absolute bottom-1/4 right-10 w-2 h-2 bg-indigo-500 rounded-full opacity-10 animate-float-dots" 
             style={{ animationDelay: '-5s' }} />
        
        {/* Floating lines */}
        <div className="absolute top-1/4 left-0 h-px w-36 bg-gradient-to-r from-transparent via-indigo-400 to-transparent opacity-10 animate-float-lines" />
        <div className="absolute bottom-1/3 right-0 h-px w-28 bg-gradient-to-l from-transparent via-primary-400 to-transparent opacity-8 animate-float-lines" 
             style={{ animationDelay: '-3s' }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <AnimatedSection>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-slate-100 mb-4">
              Travaillons Ensemble
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-indigo-500 to-primary-500 mx-auto mb-8"></div>
            <p className="text-xl text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
              Une idée de projet ? Discutons ensemble de vos besoins et donnons vie à votre vision
            </p>
          </div>
        </AnimatedSection>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Informations de contact */}
          <AnimatedSection direction="left">
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-6">
                  Restons en contact
                </h3>
                <p className="text-slate-600 dark:text-slate-300 mb-8 leading-relaxed">
                  Je suis toujours ouvert aux nouvelles opportunités et aux projets innovants. 
                  N'hésitez pas à me contacter pour discuter de vos besoins ou simplement pour échanger 
                  sur les dernières technologies.
                </p>
              </div>

              <div className="space-y-4">
                <motion.div
                  className="flex items-center gap-4 p-6 bg-white dark:bg-slate-800 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors border border-slate-200 dark:border-slate-700 group"
                  whileHover={{ scale: 1.02, x: 5 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <motion.div 
                    className="p-3 bg-gradient-to-r from-indigo-600 to-primary-600 rounded-lg text-white shadow-lg"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                  >
                    <Mail size={20} />
                  </motion.div>
                  <div>
                    <div className="font-semibold text-slate-900 dark:text-slate-100">Email</div>
                    <a 
                      href="mailto:enzo.marques@efrei.net"
                      className="text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                    >
                      enzo.marques@efrei.net
                    </a>
                  </div>
                </motion.div>

                <motion.div
                  className="flex items-center gap-4 p-6 bg-white dark:bg-slate-800 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors border border-slate-200 dark:border-slate-700 group"
                  whileHover={{ scale: 1.02, x: 5 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <motion.div 
                    className="p-3 bg-gradient-to-r from-indigo-600 to-primary-600 rounded-lg text-white shadow-lg"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                  >
                    <Phone size={20} />
                  </motion.div>
                  <div>
                    <div className="font-semibold text-slate-900 dark:text-slate-100">Téléphone</div>
                    <a 
                      href="tel:+33613236903"
                      className="text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                    >
                      +33 6 13 23 69 03
                    </a>
                  </div>
                </motion.div>

                <motion.div
                  className="flex items-center gap-4 p-6 bg-white dark:bg-slate-800 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors border border-slate-200 dark:border-slate-700 group"
                  whileHover={{ scale: 1.02, x: 5 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <motion.div 
                    className="p-3 bg-gradient-to-r from-indigo-600 to-primary-600 rounded-lg text-white shadow-lg"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                  >
                    <MapPin size={20} />
                  </motion.div>
                  <div>
                    <div className="font-semibold text-slate-900 dark:text-slate-100">Localisation</div>
                    <div className="text-slate-600 dark:text-slate-300">Sceaux, France</div>
                  </div>
                </motion.div>
              </div>

              <div className="pt-8">
                <motion.button
                  onClick={handleDownloadCV}
                  className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-indigo-600 to-primary-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300 border border-indigo-500/30 group"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <motion.div
                    whileHover={{ y: -1 }}
                  >
                    <Download size={20} />
                  </motion.div>
                  Télécharger mon CV
                </motion.button>
              </div>
            </div>
          </AnimatedSection>

          {/* Formulaire de contact */}
          <AnimatedSection direction="right">
            <motion.form
              onSubmit={handleSubmit}
              className="space-y-6 bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
                    <User size={16} className="inline mr-2" />
                    Nom *
                  </label>
                  <motion.input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-xl bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 ${
                      errors.name ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
                    }`}
                    placeholder="Votre nom"
                    whileFocus={{ scale: 1.02 }}
                  />
                  {errors.name && (
                    <motion.p 
                      className="text-red-500 text-sm mt-1 flex items-center gap-1"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <AlertCircle size={16} />
                      {errors.name}
                    </motion.p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
                    <Mail size={16} className="inline mr-2" />
                    Email *
                  </label>
                  <motion.input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-xl bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 ${
                      errors.email ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
                    }`}
                    placeholder="votre@email.com"
                    whileFocus={{ scale: 1.02 }}
                  />
                  {errors.email && (
                    <motion.p 
                      className="text-red-500 text-sm mt-1 flex items-center gap-1"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <AlertCircle size={16} />
                      {errors.email}
                    </motion.p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
                  <MessageSquare size={16} className="inline mr-2" />
                  Sujet *
                </label>
                <motion.input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-xl bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 ${
                    errors.subject ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
                  }`}
                  placeholder="Sujet de votre message"
                  whileFocus={{ scale: 1.02 }}
                />
                {errors.subject && (
                  <motion.p 
                    className="text-red-500 text-sm mt-1 flex items-center gap-1"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <AlertCircle size={16} />
                    {errors.subject}
                  </motion.p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
                  <MessageSquare size={16} className="inline mr-2" />
                  Message *
                </label>
                <motion.textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={6}
                  className={`w-full px-4 py-3 border rounded-xl bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 resize-none ${
                    errors.message ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
                  }`}
                  placeholder="Décrivez votre projet ou votre demande..."
                  whileFocus={{ scale: 1.02 }}
                />
                {errors.message && (
                  <motion.p 
                    className="text-red-500 text-sm mt-1 flex items-center gap-1"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <AlertCircle size={16} />
                    {errors.message}
                  </motion.p>
                )}
              </div>

              {/* Success message */}
              <AnimatePresence>
                {isSubmitted && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.8, y: -10 }}
                    className="p-4 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-xl"
                  >
                    <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                      <motion.div
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 0.5 }}
                      >
                        <CheckCircle size={20} />
                      </motion.div>
                      <span className="font-medium">Message envoyé avec succès !</span>
                    </div>
                    <p className="text-emerald-600 dark:text-emerald-400 text-sm mt-1">
                      Je vous répondrai dans les plus brefs délais.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Submit button */}
              <motion.button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-gradient-to-r from-indigo-600 to-primary-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 border border-indigo-500/30 relative overflow-hidden group"
                whileHover={!isSubmitting ? { scale: 1.02, y: -2 } : {}}
                whileTap={!isSubmitting ? { scale: 0.98 } : {}}
              >
                {/* Button shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700" />
                
                {isSubmitting ? (
                  <>
                    <motion.div 
                      className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <motion.div
                      whileHover={{ x: 2 }}
                    >
                      <Send size={20} />
                    </motion.div>
                    Envoyer le message
                  </>
                )}
              </motion.button>
            </motion.form>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};